import { useRef, useState, useEffect, useMemo, useCallback } from 'react'

import { Space } from '@tribeplatform/gql-client/types'
import { useSpacesByIds } from '@tribeplatform/react-sdk/hooks'

import { useI18n } from '../i18n/index.js'

import { SpacePicker } from './SpacePicker.js'

export interface SpaceIdPickerProps {
  value: string[]
  onChange: (spaceIds: string[]) => void
  multiple?: boolean
  placeholder?: string
  noWrapper?: boolean
  invalid?: boolean
  className?: string
}

export const SpaceIdPicker = ({
  value,
  onChange,
  multiple = false,
  placeholder: placeholderProp,
  noWrapper,
  invalid,
  className,
}: SpaceIdPickerProps) => {
  const { $t } = useI18n()
  const [spaces, setSpaces] = useState<Space[]>([])
  const didLoadInitialData = useRef(value.length === 0)
  const { data: initialSpaces, isInitialLoading } = useSpacesByIds({
    variables: { ids: value },
    fields: {
      image: 'basic',
    },
    useQueryOptions: {
      enabled: value.length > 0 && !didLoadInitialData.current,
      placeholderData: undefined,
    },
  })

  useEffect(() => {
    if (!didLoadInitialData.current && initialSpaces) {
      setSpaces(initialSpaces)
      didLoadInitialData.current = true
    }
  }, [initialSpaces])

  useEffect(() => {
    if (value.length === 0) {
      setSpaces([])
    }
  }, [value])

  const placeholder = useMemo(() => {
    if (isInitialLoading) {
      return $t({
        id: 'Generics.LoadingDotDotDot',
        defaultMessage: 'Loading...',
      })
    }

    return placeholderProp
  }, [isInitialLoading, placeholderProp, $t])

  const handleChanges = useCallback(
    (value: Space | Space[]) => {
      if (!value) {
        setSpaces([])
        onChange([])
        return
      }
      const spacesArray = Array.isArray(value) ? value : [value]

      setSpaces(spacesArray)
      onChange(spacesArray.map(space => space.id))
    },
    [onChange],
  )

  if (multiple) {
    return (
      <SpacePicker
        value={spaces}
        onChange={handleChanges}
        multiple
        disabled={isInitialLoading}
        placeholder={placeholder}
        noWrapper={noWrapper}
        className={className}
        invalid={invalid}
      />
    )
  }
  return (
    <SpacePicker
      value={spaces?.at(0)}
      onChange={handleChanges}
      disabled={isInitialLoading}
      placeholder={placeholder}
      noWrapper={noWrapper}
      className={className}
      invalid={invalid}
    />
  )
}
